import { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  VStack,
  Text,
  Box,
  Alert,
  AlertIcon,
  useColorModeValue,
} from '@chakra-ui/react';

import {
  CONSENT_METHOD_VERBAL,
  CONSENT_METHOD_WRITTEN,
  CONSENT_TYPE_DASH_HISTORY,
} from '../constants';

export { CONSENT_TYPE_DASH_HISTORY };

interface DashConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (method: number) => void;
  isLoading?: boolean;
  driverName: string;
  licenseNumber: string;
}

const DashConsentModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  driverName,
  licenseNumber,
}: DashConsentModalProps) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const disclaimerBg = useColorModeValue('gray.50', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [method, setMethod] = useState<string>(String(CONSENT_METHOD_VERBAL));

  const handleConfirm = () => {
    onConfirm(parseInt(method, 10));
  };

  const isValid = driverName.trim() !== '' && licenseNumber.trim() !== '';

  const handleClose = () => {
    setMethod(String(CONSENT_METHOD_VERBAL));
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>
          Client Consent for DASH Report
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                Driver
              </Text>
              <Text fontSize="md">{driverName || '—'}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                License Number
              </Text>
              <Text fontSize="md">{licenseNumber || '—'}</Text>
            </Box>

            <Box
              p={4}
              bg={disclaimerBg}
              borderRadius="md"
              borderWidth="1px"
              borderColor={borderColor}
            >
              <Text
                fontSize="sm"
                color="gray.600"
                _dark={{ color: 'gray.400' }}
              >
                I confirm that I have received authorization from the client to
                obtain, verify, and exchange personal information for the
                purposes of underwriting, rating, and issuing insurance,
                including my claims history report from the appropriate
                authorities and databases.
              </Text>
            </Box>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Consent Method
              </FormLabel>
              <RadioGroup value={method} onChange={setMethod}>
                <VStack align="start" spacing={2}>
                  <Radio
                    value={String(CONSENT_METHOD_VERBAL)}
                    colorScheme="blue"
                  >
                    Verbal Consent
                  </Radio>
                  <Radio
                    value={String(CONSENT_METHOD_WRITTEN)}
                    colorScheme="blue"
                  >
                    Written Consent
                  </Radio>
                </VStack>
              </RadioGroup>
            </FormControl>

            {!isValid && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                Driver name and license number must be saved before confirming
                consent.
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleConfirm}
            isLoading={isLoading}
            isDisabled={!isValid}
          >
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DashConsentModal;
